import { Controller } from "@hotwired/stimulus"
import { parseGIF, decompressFrames } from 'gifuct-js'
import {
  isAllowedBasePhoto,
  isAllowedTargetMedia as supportsTargetMedia,
  targetMediaTypeFor
} from '../constants/file_types'
import { registerAndPrepareTargetFiles } from '../lib/target_file_registration.mjs'

export default class extends Controller {
  static targets = ["basePhoto", "targetContainer", "targetPlaceholder", "baseFileName"]
  static values = {
    maxFiles: { type: Number, default: 10 },
    maxFileSize: { type: Number, default: 100 * 1024 * 1024 }, // 100MB in bytes
    copy: { type: Object, default: {} }
  }

  connect() {
    this.files = new Set()
    this.fileMetadata = new Map()
    this.fileIds = new Map()
    this.bindDragAndDrop()
  }

  disconnect() {
    this.unbindDragAndDrop()
  }

  bindDragAndDrop() {
    this.handleDragOver = this.handleDragOver.bind(this)
    this.handleDrop = this.handleDrop.bind(this)
    this.handleDragEnter = this.handleDragEnter.bind(this)
    this.handleDragLeave = this.handleDragLeave.bind(this)
    this.handleDragStart = this.handleDragStart.bind(this)
    
    // Add listeners to both base photo and target media dropzones
    this.basePhotoTarget.addEventListener("dragover", this.handleDragOver)
    this.basePhotoTarget.addEventListener("drop", this.handleDrop)
    this.basePhotoTarget.addEventListener("dragenter", this.handleDragEnter)
    this.basePhotoTarget.addEventListener("dragleave", this.handleDragLeave)
    this.basePhotoTarget.addEventListener("dragstart", this.handleDragStart)
    
    const targetDropzone = this.targetContainerTarget.parentElement
    targetDropzone.addEventListener("dragover", this.handleDragOver)
    targetDropzone.addEventListener("drop", this.handleDrop)
    targetDropzone.addEventListener("dragenter", this.handleDragEnter)
    targetDropzone.addEventListener("dragleave", this.handleDragLeave)
    targetDropzone.addEventListener("dragstart", this.handleDragStart)
  }

  unbindDragAndDrop() {
    if (!this.handleDragOver || !this.handleDrop) return

    this.basePhotoTarget.removeEventListener("dragover", this.handleDragOver)
    this.basePhotoTarget.removeEventListener("drop", this.handleDrop)
    this.basePhotoTarget.removeEventListener("dragenter", this.handleDragEnter)
    this.basePhotoTarget.removeEventListener("dragleave", this.handleDragLeave)
    this.basePhotoTarget.removeEventListener("dragstart", this.handleDragStart)
    
    const targetDropzone = this.targetContainerTarget.parentElement
    if (targetDropzone) {
      targetDropzone.removeEventListener("dragover", this.handleDragOver)
      targetDropzone.removeEventListener("drop", this.handleDrop)
      targetDropzone.removeEventListener("dragenter", this.handleDragEnter)
      targetDropzone.removeEventListener("dragleave", this.handleDragLeave)
      targetDropzone.removeEventListener("dragstart", this.handleDragStart)
    }
  }

  handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    
    const files = Array.from(e.dataTransfer.files)
    if (!files.length) return

    // Determine which dropzone received the drop
    if (e.currentTarget === this.basePhotoTarget && files.length === 1) {
      this.handleBasePhotoDrop(files[0])
    } else {
      this.handleTargetMediaDrop(files)
    }
  }

  handleBasePhotoDrop(file) {
    // Check file type for base photo
    if (!isAllowedBasePhoto(file)) {
      this.showError(this.localizedCopy("base_photo_unsupported_type"))
      // Clear any existing preview
      this.resetBasePhoto()
      return
    }

    // Check file size limit
    if (file.size > this.maxFileSizeValue) {
      this.showError(this.localizedCopy("base_photo_too_large", { name: file.name }))
      this.resetBasePhoto()
      return
    }
    
    // Clear any existing errors when valid file is dropped
    this.clearErrors()
    
    // Create a new FileList-like object
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    
    // Update the file input
    const fileInput = this.basePhotoTarget.querySelector('input[type="file"]')
    fileInput.files = dataTransfer.files
    
    // Trigger the load event
    this.loadBasePhoto({ target: fileInput })
  }

  async handleTargetMediaDrop(files) {
    // Check total files limit before proceeding
    if (this.files.size + files.length > this.maxFilesValue) {
      this.showError(this.localizedCopy("too_many_target_files", { count: this.maxFilesValue }), "target")
      return
    }
    
    // Check file type and size limits before uploading anything.
    for (const file of files) {
      if (!this.isAllowedTargetMedia(file)) {
        this.showError(`${file.name}: ${this.copyValue.unsupported_target_media_type}`, "target")
        return
      }

      if (file.size > this.maxFileSizeValue) {
        this.showError(`${file.name}: ${this.copyValue.file_too_large}`, "target")
        return
      }
    }

    this.clearErrors("target")

    await this.registerTargetFiles(files)

    this.updatePlaceholder()
    await this.dispatchCostCalculation()
  }

  loadBasePhoto(event) {
    const file = event.target.files[0]
    if (!file) return

    // Validate file type
    if (!isAllowedBasePhoto(file)) {
      this.showError(this.localizedCopy("base_photo_unsupported_type"))
      event.target.value = ''
      this.resetBasePhoto()
      return
    }

    // Clear any existing errors when valid file is selected
    this.clearErrors()

    if (file.size > this.maxFileSizeValue) {
      this.showError(this.localizedCopy("base_photo_too_large", { name: file.name }))
      event.target.value = ''
      this.resetBasePhoto()
      return
    }

    this.baseFileNameTarget.textContent = file.name
    this.baseFileNameTarget.style.display = "block"
    this.loadPreview(file, this.basePhotoTarget)
  }

  async loadTargetMedia(event) {
    const newFiles = Array.from(event.target.files)
    const fileInput = event.target
    
    if (this.files.size + newFiles.length > this.maxFilesValue) {
      this.showError(this.localizedCopy("too_many_target_files", { count: this.maxFilesValue }), "target")
      return
    }
    
    // Check file type and size limits before uploading anything.
    for (const file of newFiles) {
      if (!this.isAllowedTargetMedia(file)) {
        this.showError(`${file.name}: ${this.copyValue.unsupported_target_media_type}`, "target")
        fileInput.value = ''
        return
      }

      if (file.size > this.maxFileSizeValue) {
        this.showError(`${file.name}: ${this.copyValue.file_too_large}`, "target")
        // Reset the file input to clear the selection
        fileInput.value = ''
        return
      }
    }

    this.clearErrors("target")

    // Register every picker entry before metadata work can pause this handler.
    // This disables submission immediately and keeps every selected file in the
    // direct-upload serializer while slow video/GIF metadata loads in parallel.
    await this.registerTargetFiles(newFiles)

    fileInput.value = ""

    this.updatePlaceholder()
    await this.dispatchCostCalculation()
  }

  async processFileMetadata(file) {
    const mediaType = targetMediaTypeFor(file)
    if (mediaType === "image/gif") {
      const duration = await this.getGifDuration(file)
      this.fileMetadata.set(file.name, { duration })
    } else if (mediaType.startsWith("video/")) {
      const duration = await this.getVideoDuration(file)
      this.fileMetadata.set(file.name, { duration })
    }
  }

  registerTargetFiles(files) {
    return registerAndPrepareTargetFiles(files, {
      isRegistered: (file) => this.files.has(file),
      register: (file) => this.files.add(file),
      createPreview: (file) => this.createPreviewCard(file),
      dispatchFileType: (file) => this.dispatchFileType(targetMediaTypeFor(file)),
      processMetadata: (file) => this.processFileMetadata(file),
      metadataReady: (file) => this.updateDurationBadge(file)
    })
  }

  /**
   * Updated GIF duration logic.
   * 1. Parse the GIF data
   * 2. Sum the delays for one full loop
   * 3. If we detect suspiciously large or infinite loops, fallback to a practical 0.1s/frame approach
   */
  async getGifDuration(file) {
    return new Promise((resolve) => {
      const reader = new FileReader()
      
      reader.onload = async (e) => {
        try {
          const buffer = e.target.result
          const gif = parseGIF(buffer)
          const frames = decompressFrames(gif, true)

          // Get duration in seconds (convert from ms)
          const durationMs = frames.reduce((acc, frame) => {
            return acc + (frame.delay || 10)
          }, 0)
          const durationSeconds = Number((durationMs / 1000).toFixed(2))
          
          resolve(durationSeconds)
        } catch (error) {
          resolve(3) // Keep fallback for completely corrupt files
        }
      }

      reader.onerror = () => resolve(3)
      
      reader.readAsArrayBuffer(file)
    })
  }

  getVideoDuration(file) {
    return new Promise((resolve) => {
      const video = document.createElement("video")
      video.preload = "metadata"
      const objectUrl = URL.createObjectURL(file)
      let settled = false

      const finish = (duration = 0) => {
        if (settled) return

        settled = true
        clearTimeout(timeoutId)
        URL.revokeObjectURL(objectUrl)
        resolve(duration)
      }
      const timeoutId = setTimeout(() => finish(), 10_000)
      
      video.onloadedmetadata = () => {
        const durationInSeconds = Number.isFinite(video.duration) ? Number(video.duration.toFixed(2)) : 0
        finish(durationInSeconds)
      }
      video.onerror = () => finish()
      
      video.src = objectUrl
    })
  }

  isAllowedTargetMedia(file) {
    return supportsTargetMedia(file)
  }

  removeFile(event) {
    const card = event.target.closest(".preview-card")
    const file = Array.from(this.files).find((candidate) => this.fileIds.get(candidate) === card.dataset.fileId)
    if (!file) return

    window.dispatchEvent(new CustomEvent("target-picker:file-removed", {
      detail: { file, card, form: this.element }
    }))
    this.files.delete(file)
    this.fileIds.delete(file)
    this.fileMetadata.delete(file.name)
    
    card.remove()
    this.updatePlaceholder()
    this.dispatchCostCalculation()
  }

  updatePlaceholder() {
    if (this.files.size === 0) {
      this.targetPlaceholderTarget.style.display = "flex"
    } else {
      this.targetPlaceholderTarget.style.display = "none"
    }
  }

  createPreviewCard(file) {
    const fileId = window.crypto?.randomUUID?.() || `target-${Date.now()}-${Math.random()}`
    this.fileIds.set(file, fileId)
    const card = document.createElement("div")
    card.className = "preview-card relative overflow-hidden rounded-xl border border-gray-700 bg-gray-900 shadow-lg"
    card.dataset.fileName = file.name
    card.dataset.fileId = fileId

    const preview = document.createElement("div")
    preview.className = "target-picker-preview relative h-52 w-full bg-gray-950 bg-contain bg-center bg-no-repeat"

    const overlay = document.createElement("div")
    overlay.className = "target-picker-overlay pointer-events-none absolute inset-0"
    preview.appendChild(overlay)
    
    const removeButton = document.createElement("button")
    removeButton.className = "absolute right-3 top-3 z-30 flex h-9 w-9 items-center justify-center rounded-full bg-gray-950/85 text-xl text-white shadow-lg transition hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300"
    removeButton.textContent = "×"
    removeButton.type = "button"
    removeButton.setAttribute("aria-label", this.localizedCopy("remove_file_aria", { name: file.name }))
    removeButton.addEventListener("click", (e) => this.removeFile(e))

    const fileName = document.createElement("div")
    fileName.className = "absolute bottom-0 left-0 right-0 z-20 truncate bg-gray-950/80 px-3 py-2 text-xs text-white backdrop-blur-sm"
    fileName.textContent = file.name

    // Add duration badge for videos and GIFs
    const mediaType = targetMediaTypeFor(file)
    if (mediaType.startsWith("video/") || mediaType === "image/gif") {
      const duration = this.fileMetadata.get(file.name)?.duration
      if (duration) {
        const durationBadge = document.createElement("div")
        durationBadge.className = "absolute left-3 top-3 z-20 rounded-full bg-black/75 px-2 py-1 text-xs text-white"
        durationBadge.dataset.durationBadge = "true"
        durationBadge.textContent = `${Math.ceil(duration)}s`
        card.appendChild(durationBadge)
      }
    }

    card.appendChild(preview)
    card.appendChild(removeButton)
    card.appendChild(fileName)
    const controls = document.createElement("div")
    controls.className = "target-picker-controls min-h-32 border-t border-gray-800 p-4"
    card.appendChild(controls)
    this.targetContainerTarget.appendChild(card)

    this.loadPreview(file, preview)
    window.dispatchEvent(new CustomEvent("target-picker:file-added", {
      detail: { file, card, form: this.element }
    }))
  }

  updateDurationBadge(file) {
    const duration = this.fileMetadata.get(file.name)?.duration
    const fileId = this.fileIds.get(file)
    const card = Array.from(this.targetContainerTarget.children)
      .find((candidate) => candidate.dataset.fileId === fileId)
    if (!duration || !card || card.querySelector("[data-duration-badge]")) return

    const durationBadge = document.createElement("div")
    durationBadge.className = "absolute left-3 top-3 z-20 rounded-full bg-black/75 px-2 py-1 text-xs text-white"
    durationBadge.dataset.durationBadge = "true"
    durationBadge.textContent = `${Math.ceil(duration)}s`
    card.appendChild(durationBadge)
  }

  loadPreview(file, element) {
    if (targetMediaTypeFor(file).startsWith("video/")) {
      const video = document.createElement("video")
      video.preload = "metadata"
      const objectUrl = URL.createObjectURL(file)
      
      video.addEventListener("loadeddata", () => {
        video.currentTime = 0
      })

      video.addEventListener("seeked", () => {
        const canvas = document.createElement("canvas")
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        
        const ctx = canvas.getContext("2d")
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        
        element.style.backgroundImage = `url('${canvas.toDataURL("image/jpeg", 0.8)}')`
        element.style.backgroundSize = "contain"
        element.style.backgroundPosition = "center"
        
        URL.revokeObjectURL(objectUrl)
      })

      video.src = objectUrl
    } else {
      const reader = new FileReader()
      reader.onload = (e) => {
        element.style.backgroundImage = `url('${e.target.result}')`
        element.style.backgroundSize = "contain"
        element.style.backgroundPosition = "center"
      }
      reader.readAsDataURL(file)
    }
  }

  dispatchFileType(fileType) {
    const event = new CustomEvent("upscale:filetype", {
      detail: { fileType }
    })
    window.dispatchEvent(event)
  }

  async dispatchCostCalculation() {
    // Ensure all metadata is loaded before dispatching
    const filesWithMetadata = await Promise.all(
      Array.from(this.files).map(async (file) => {
        let metadata = this.fileMetadata.get(file.name)
        const mediaType = targetMediaTypeFor(file)

        // If metadata doesn't exist yet, process it
        if (!metadata && (mediaType === "image/gif" || mediaType.startsWith("video/"))) {
          const duration = mediaType === "image/gif"
            ? await this.getGifDuration(file)
            : await this.getVideoDuration(file)
          metadata = { duration }
          this.fileMetadata.set(file.name, metadata)
        }

        return {
          file,
          metadata: metadata || {}
        }
      })
    )

    const event = new CustomEvent("files:changed", {
      detail: { files: filesWithMetadata },
      bubbles: true
    })
    window.dispatchEvent(event)
  }

  handleDragEnter(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  handleDragStart(e) {
    e.preventDefault()
    e.stopPropagation()
    return false
  }

  resetBasePhoto() {
    // Reset base photo dropzone
    this.basePhotoTarget.style.backgroundImage = "none"
    this.baseFileNameTarget.style.display = "none"
    this.baseFileNameTarget.textContent = ""
    // Dispatch event for base-photo-selector to handle its own placeholder
    const event = new CustomEvent('base-photo:reset', { bubbles: true })
    this.basePhotoTarget.dispatchEvent(event)
  }

  localizedCopy(key, replacements = {}) {
    return Object.entries(replacements).reduce(
      (message, [name, value]) => message.replaceAll(`%{${name}}`, value),
      this.copyValue[key] || ""
    )
  }

  showError(message, section = "base") {
    // Find the form
    const form = this.element.closest('form')
    if (!form) return

    const targetMedia = section === "target"
    const errorClass = targetMedia ? "target-media-errors" : "base-photo-errors"
    const sectionSelector = targetMedia
      ? "[data-multiple-dropzone-target-error-section]"
      : "[data-multiple-dropzone-error-section]"
    let errorContainer = form.querySelector(`.${errorClass}`)
    if (!errorContainer) {
      const errorSection = form.querySelector(sectionSelector)
      if (errorSection) {
        // Create error container if it doesn't exist
        errorContainer = document.createElement('div')
        errorContainer.className = `${errorClass} text-red-500 mt-2`
        errorContainer.setAttribute('role', 'alert')
        // Insert after the label but before the dropzone
        const labelContainer = errorSection.querySelector('.flex.items-center.mb-2')
        if (labelContainer && labelContainer.nextSibling) {
          labelContainer.parentNode.insertBefore(errorContainer, labelContainer.nextSibling)
        }
      }
    }
    
    if (errorContainer) {
      // Create persistent error message (like Rails form errors)
      const errorP = document.createElement('p')
      errorP.className = 'text-sm text-red-500 mt-1'
      errorP.textContent = message
      
      // Clear existing errors and add new one
      errorContainer.innerHTML = ''
      errorContainer.appendChild(errorP)
      
    }

    // Always provide feedback even if the inline error hook is unavailable.
    this.showToaster(message)
  }

  showToaster(message) {
    // Create toaster notification
    const toaster = document.createElement('div')
    toaster.className = 'fixed top-4 right-4 z-50 bg-red-900 bg-opacity-90 border border-red-700 text-red-100 px-6 py-4 rounded-lg shadow-lg max-w-md'
    
    // Create inner structure safely without innerHTML
    const container = document.createElement('div')
    container.className = 'flex items-start'
    
    const textContainer = document.createElement('div')
    textContainer.className = 'flex-1'
    
    const title = document.createElement('p')
    title.className = 'text-sm font-medium'
    title.textContent = this.copyValue.validation_error_title
    
    const messageP = document.createElement('p')
    messageP.className = 'text-sm mt-1'
    messageP.textContent = message // Safe from XSS
    
    textContainer.appendChild(title)
    textContainer.appendChild(messageP)
    
    const closeButton = document.createElement('button')
    closeButton.className = 'ml-4 text-red-300 hover:text-red-100'
    closeButton.innerHTML = `
      <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
      </svg>
    `
    
    // Bind click handler properly (no inline onclick)
    closeButton.addEventListener('click', () => toaster.remove())
    
    container.appendChild(textContainer)
    container.appendChild(closeButton)
    toaster.appendChild(container)
    
    document.body.appendChild(toaster)
    
    // Auto-remove after 10 seconds (longer timeout)
    setTimeout(() => {
      if (toaster.parentElement) {
        toaster.remove()
      }
    }, 10000)
  }

  clearErrors(section = "base") {
    // Clear form errors
    const form = this.element.closest('form')
    if (form) {
      const errorClass = section === "target" ? "target-media-errors" : "base-photo-errors"
      const errorContainer = form.querySelector(`.${errorClass}`)
      if (errorContainer) {
        errorContainer.innerHTML = ''
      }
    }
  }
}
